import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/lib/store';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ShieldCheck, Sparkles, Eye, EyeOff } from 'lucide-react';

const AUTH_BASE = 'https://auth.emergentagent.com/';

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();

  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleGoogle = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `${AUTH_BASE}?redirect=${encodeURIComponent(redirect)}`;
  };

  const validateInputs = () => {
    if (!email.trim() || !email.includes('@') || !email.includes('.')) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!password) {
      toast.error('Please enter your password');
      return false;
    }
    if (mode === 'signup' && password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateInputs()) return;
    setBusy(true);
    try {
      const url = mode === 'signup' ? '/auth/signup' : '/auth/login';
      const body = { email: email.trim().toLowerCase(), password };
      if (mode === 'signup' && name.trim()) body.name = name.trim();
      const r = await api.post(url, body);
      setUser(r.data.user);
      toast.success(mode === 'signup' ? `Welcome to Memoir, ${r.data.user.name}` : `Welcome back, ${r.data.user.name}`);
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || (mode === 'signup' ? 'Sign up failed' : 'Sign in failed');
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-dvh relative" data-testid="login-page">
      <img src="https://images.unsplash.com/photo-1600168488589-d88081e77cd0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-background" />
      <div className="relative z-10 min-h-dvh flex flex-col">
        <div className="flex-1 flex items-end justify-center px-5 pb-2">
          <div className="max-w-sm w-full text-white pt-16 sm:pt-24 pb-6">
            <div className="flex items-center gap-3 mb-3">
              <img src="/logo-icon.png" alt="Memoir" className="h-12 w-12 rounded-xl bg-white/95 p-1.5 shadow-lg" />
              <div>
                <div className="font-serif text-2xl font-semibold">Memoir<sup className="text-[10px] align-super ml-0.5">™</sup></div>
                <div className="text-xs text-white/80">Capture. Share. Remember.</div>
              </div>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold leading-tight">
              Memorable trips,<br />beautifully kept.
            </h2>
          </div>
        </div>

        <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }} className="px-3 pb-6">
          <Card className="max-w-sm mx-auto rounded-3xl border bg-card shadow-[0_24px_60px_-22px_rgba(15,23,42,0.45)] p-5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4" data-testid="login-security-copy">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))]" />
              <span>Private by default. You control sharing.</span>
            </div>

            <Button onClick={handleGoogle} data-testid="login-google-button" className="w-full h-11 rounded-xl text-sm font-semibold gradient-fab text-white hover:brightness-105 active:scale-[0.99]">
              <Sparkles className="h-4 w-4 mr-2" />
              Continue with Google
            </Button>

            <div className="my-4 flex items-center gap-2">
              <div className="h-px bg-border flex-1" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">or with email</span>
              <div className="h-px bg-border flex-1" />
            </div>

            {/* Mode tabs */}
            <div className="inline-flex rounded-2xl bg-secondary p-1 gap-1 w-full mb-3" data-testid="auth-mode-tabs">
              <button
                type="button"
                data-testid="auth-mode-signin"
                onClick={() => setMode('signin')}
                className={`flex-1 px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${mode === 'signin' ? 'bg-card shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
              >Sign in</button>
              <button
                type="button"
                data-testid="auth-mode-signup"
                onClick={() => setMode('signup')}
                className={`flex-1 px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${mode === 'signup' ? 'bg-card shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
              >Sign up</button>
            </div>

            <form onSubmit={onSubmit} className="space-y-3" data-testid="auth-form">
              {mode === 'signup' && (
                <div>
                  <Label htmlFor="name" className="text-xs">Name <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    data-testid="auth-name-input"
                    autoComplete="name"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-xs">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="auth-email-input"
                  autoComplete="email"
                  inputMode="email"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs">Password {mode === 'signup' ? <span className="text-muted-foreground font-normal">· min 8 chars</span> : null}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'signup' ? 'Create a password' : 'Your password'}
                    data-testid="auth-password-input"
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                    data-testid="auth-password-toggle"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md hover:bg-secondary text-muted-foreground flex items-center justify-center"
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="auth-submit-button"
                className="w-full h-11 rounded-xl gradient-fab text-white font-semibold"
              >
                {busy ? (mode === 'signup' ? 'Creating account…' : 'Signing in…') : (mode === 'signup' ? 'Create account' : 'Sign in')}
              </Button>
            </form>

            <p className="mt-4 text-[11px] text-muted-foreground text-center">
              {mode === 'signup' ? (
                <>By creating an account you agree to our <span className="underline">Terms</span> and <span className="underline">Privacy</span>.</>
              ) : (
                <>New here? <button type="button" data-testid="switch-to-signup" onClick={() => setMode('signup')} className="font-semibold text-[hsl(var(--primary))] hover:underline">Create an account</button></>
              )}
            </p>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
